import { useCallback, useSyncExternalStore } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const USER_OVERRIDE_TTL_MS = 30 * 60 * 1000;
const AUTO_COLLAPSE_QUERY = '(max-width: 1279.98px)';

interface SidebarCollapseState {
  collapsed: boolean;
  lastUserToggleAt: number;
  setCollapsed: (next: boolean) => void;
}

export const useSidebarCollapseStore = create<SidebarCollapseState>()(
  persist(
    (set) => ({
      collapsed: false,
      lastUserToggleAt: 0,
      setCollapsed: (next: boolean) => set({ collapsed: next, lastUserToggleAt: Date.now() }),
    }),
    { name: 'loki-sidebar-user-collapse' },
  ),
);

function subscribeMatch(query: string, cb: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(query);
  mql.addEventListener('change', cb);
  return () => mql.removeEventListener('change', cb);
}

function getMatch(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

function useAutoCollapse(): boolean {
  return useSyncExternalStore(
    (cb) => subscribeMatch(AUTO_COLLAPSE_QUERY, cb),
    () => getMatch(AUTO_COLLAPSE_QUERY),
    () => false,
  );
}

/**
 * Subscribe to "is the user override still within the 30-min TTL" using
 * useSyncExternalStore. When the store mutates we re-evaluate; we also schedule
 * a single setTimeout to fire exactly when the TTL elapses so the component
 * re-renders without polling.
 */
function useOverrideActive(lastToggle: number): boolean {
  return useSyncExternalStore(
    (notify) => {
      const unsubStore = useSidebarCollapseStore.subscribe(() => notify());
      let timer: ReturnType<typeof setTimeout> | undefined;
      if (lastToggle > 0) {
        const remaining = USER_OVERRIDE_TTL_MS - (Date.now() - lastToggle);
        if (remaining > 0) timer = setTimeout(notify, remaining);
      }
      return () => {
        unsubStore();
        if (timer) clearTimeout(timer);
      };
    },
    () => lastToggle > 0 && Date.now() - lastToggle < USER_OVERRIDE_TTL_MS,
    () => false,
  );
}

export function useSidebarCollapse() {
  const stored = useSidebarCollapseStore((s) => s.collapsed);
  const lastToggle = useSidebarCollapseStore((s) => s.lastUserToggleAt);
  const setCollapsed = useSidebarCollapseStore((s) => s.setCollapsed);
  const autoCollapsed = useAutoCollapse();
  const overrideActive = useOverrideActive(lastToggle);

  const collapsed = overrideActive ? stored : autoCollapsed || stored;

  const toggle = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  return { collapsed, toggle };
}
